<script setup lang="ts">
import { computed } from "vue";

const isToggled = defineModel<boolean>("isToggled");

const {
  disabled = false,
  leftLabel,
  rightLabel,
  leftIcon,
  rightIcon,
  ariaLabel,
} = defineProps<{
  disabled?: boolean;
  leftLabel?: string;
  rightLabel?: string;
  leftIcon?: string;
  rightIcon?: string;
  ariaLabel?: string;
}>();

const currentState = computed(() => isToggled.value);

const computedAriaLabel = computed(
  () => ariaLabel ?? `Toggle: ${currentState.value ? "On" : "Off"}`
);

const toggle = () => {
  if (!disabled) {
    isToggled.value = !isToggled.value;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (disabled) return;

  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    toggle();
  }
};
</script>

<template>
  <div
    class="large-slide-toggle"
    :class="{ disabled }"
    :tabindex="disabled ? -1 : 0"
    role="button"
    :aria-label="computedAriaLabel"
    :aria-disabled="disabled"
    @click="toggle"
    @keydown="handleKeydown"
  >
    <div class="segment text-base-regular" :class="{ selected: !currentState }">
      <i v-if="leftIcon" :class="leftIcon" class="icon" />
      <span v-if="leftLabel">{{ leftLabel }}</span>
    </div>

    <div class="segment text-base-regular" :class="{ selected: currentState }">
      <i v-if="rightIcon" :class="rightIcon" class="icon" />
      <span v-if="rightLabel">{{ rightLabel }}</span>
    </div>
  </div>
</template>

<style scoped>
.large-slide-toggle {
  display: inline-flex;
  width: 100%;
  padding: var(--numbers-4);
  border-radius: var(--numbers-60);
  background-color: var(--background-tertiary);
  cursor: pointer;
  outline: none;
  transition:
    filter var(--animation-duration) var(--animation-easing),
    opacity var(--animation-duration) var(--animation-easing);

  &.disabled {
    filter: grayscale(1);
    opacity: 0.5;
    pointer-events: none;
  }

  .segment {
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    padding: var(--numbers-12);
    border-radius: var(--numbers-60);
    color: var(--content-tertiary);
    gap: var(--numbers-10);
    transition: color var(--animation-duration) var(--animation-easing);

    &.selected {
      color: var(--content-brand);
    }

    &:hover:not(.selected) {
      color: var(--content-secondary);
    }

    &:first-child::before {
      position: absolute;
      z-index: -1;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: var(--numbers-60);
      background-color: var(--background-primary);
      content: "";
      transition: left var(--animation-duration) var(--animation-easing);
    }

    &:not(.selected)::before {
      left: 100%;
    }
  }
}
</style>
